import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BRAND, LOGO_URL, TID } from "../lib/brand";

const NAV = [
  { to: "/gallery", label: "Work", tid: TID.nav.linkWork },
  { to: "/services", label: "Services", tid: TID.nav.linkServices },
  { to: "/about", label: "About", tid: TID.nav.linkAbout },
  { to: "/partners", label: "Partners", tid: TID.nav.linkPartners },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      <header
        data-testid={TID.nav.root}
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${scrolled ? "bg-ink-950/70 backdrop-blur-xl border-b border-ink-700" : "bg-transparent"}`}
      >
        <div className="wrap flex items-center justify-between h-16 md:h-20">
          <Link to="/" data-testid={TID.nav.logo} className="flex items-center gap-3 group">
            <img src={LOGO_URL} alt="ProForm Paint & Body" className="h-9 md:h-11 w-auto" />
            <span className="hidden md:inline font-mono text-[10px] tracking-[0.28em] text-chrome-300 group-hover:text-proform-red transition-colors">
              PROFORM · DENVER, CO
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.tid}
                className={({ isActive }) =>
                  `font-mono text-[11px] tracking-[0.28em] uppercase transition-colors ${
                    isActive ? "text-proform-red" : "text-chrome-100 hover:text-proform-red"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <Link to="/estimate" data-testid={TID.nav.linkEstimate} className="btn btn-primary">
              Get a Quote
              <span aria-hidden>→</span>
            </Link>
          </nav>

          <button
            data-testid={TID.nav.mobileToggle}
            aria-label="Toggle menu"
            className="md:hidden inline-flex items-center gap-2 text-chrome-100"
            onClick={() => setOpen((v) => !v)}
          >
            <span className="font-mono text-[11px] tracking-[0.28em]">{open ? "CLOSE" : "MENU"}</span>
            <span className="flex flex-col gap-1">
              <span className={`h-[2px] w-6 bg-chrome-100 transition-transform ${open ? "translate-y-[3px] rotate-45" : ""}`}></span>
              <span className={`h-[2px] w-6 bg-chrome-100 transition-transform ${open ? "-translate-y-[3px] -rotate-45" : ""}`}></span>
            </span>
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.aside
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-x-0 top-16 z-40 md:hidden border-b border-ink-700 bg-ink-950/95 backdrop-blur-xl"
          >
            <div className="wrap py-6 flex flex-col gap-4">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} className="text-2xl font-display uppercase tracking-tight hover:text-proform-red">
                  {n.label}
                </Link>
              ))}
              <Link to="/estimate" className="btn btn-primary mt-4 self-start">Get a Quote →</Link>
              <div className="pt-4 grid grid-cols-2 gap-3">
                <a href={`tel:${BRAND.phoneRaw}`} className="btn">Call</a>
                <a href={`sms:${BRAND.phoneRaw}`} className="btn">Text</a>
              </div>
            </div>
          </motion.aside>
        )}
      </AnimatePresence>
    </>
  );
}
